import React from 'react';
import { Calendar, Clock, Users, ArrowLeft } from 'lucide-react';
import { useGetAllPastEvents } from '../services';
import { Loading, Error } from '../shared/components';

const PastEventsPage: React.FC = () => {
  const { pastEvents, isLoading, error } = useGetAllPastEvents();

  // Loading state
  if (isLoading) {
    return <Loading message="Loading past events..." />;
  }

  // Error state
  if (error) {
    return <Error message={error} />;
  }

  return (
    <div className="py-6 sm:py-12">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center mb-3 sm:mb-4">
              <button
                onClick={() => window.history.back()}
                className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-primary-500" />
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-primary-500">
                Past Events
              </h1>
            </div>
            <div className="w-12 sm:w-16 h-0.5 rounded bg-primary-500 ml-14"></div>
          </div>

          {/* Events Count */}
          {pastEvents.length > 0 && (
            <div className="mb-6">
              <p className="text-lg text-secondary-700">
                Showing {pastEvents.length} past event
                {pastEvents.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {/* Events Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {pastEvents.map((event) => (
              <div
                key={event.id}
                className="bg-gray-100 p-4 sm:p-6 rounded-lg border-l-4 border-l-primary-500 cursor-pointer transition-all duration-200 hover:shadow-lg hover:bg-gray-50"
                onClick={() =>
                  event.webinarLink && window.open(event.webinarLink, '_blank')
                }
              >
                <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-secondary-700">
                  {event.title}
                </h3>

                <div className="space-y-2 sm:space-y-3 mb-4">
                  {event.dateStr && (
                    <div className="flex items-start">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                      <span className="text-sm sm:text-base text-secondary-700">
                        {event.dateStr}
                      </span>
                    </div>
                  )}
                  {(event.startTimeStr || event.endTimeStr) && (
                    <div className="flex items-start">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                      <span className="text-sm sm:text-base text-secondary-700">
                        {event.startTimeStr}
                        {event.endTimeStr && ` - ${event.endTimeStr}`}
                      </span>
                    </div>
                  )}
                  {event.speaker && (
                    <div className="flex items-start">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                      <span className="text-sm sm:text-base text-secondary-700">
                        Speaker: {event.speaker}
                      </span>
                    </div>
                  )}
                </div>

                {event.desc && (
                  <p className="text-sm sm:text-base mb-4 text-secondary-700">
                    {event.desc}
                  </p>
                )}

                <div className="flex justify-end">
                  {event.webinarLink ? (
                    <button className="text-sm underline hover:opacity-80 text-primary-500 hover:text-primary-600 transition-colors duration-200">
                      View Recording
                    </button>
                  ) : (
                    <span className="text-sm text-gray-400">
                      No recording available
                    </span>
                  )}
                </div>
              </div>
            ))}

            {/* Show message if no past events */}
            {pastEvents.length === 0 && (
              <div className="bg-gray-100 p-8 rounded-lg col-span-full text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold mb-2 text-primary-500">
                  No Past Events
                </h3>
                <p className="text-secondary-700">
                  Check back later for past event recordings and summaries.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastEventsPage;
